import { SectionSubtitle } from "@/components/SectionSubtitle";
import { SectionTitle } from "@/components/SectionTitle";
import { useMuiTheme } from "@/hooks/useMuiTheme";
import { DashedCurveDecoration } from "@/svgs";
import { Box, Container, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

export const WhyDNACanvasHeadingSection = () => {
  const theme = useMuiTheme();
  const { t, i18n } = useTranslation();

  return (
    <>
      <Box sx={{ py: { xs: 3, md: 4, lg: 5 } }}>
        <Container sx={{}}>
          {i18n.language === "th" && <SectionSubtitle>Why DNAcanvas</SectionSubtitle>}
          <SectionTitle sx={{ mt: 1 }}>{t("why-heading.title")}</SectionTitle>
        </Container>
      </Box>
      <Box
        sx={{
          backgroundColor: theme.palette.primary[100],
          borderRadius: "0 240px 0 0",
          py: 8,
          [theme.breakpoints.down("md")]: {
            borderRadius: 0,
          },
        }}
      >
        <Box sx={{ px: { xs: 0, xl: 7 } }}>
          <Container
            maxWidth={false}
            sx={{
              isolation: "isolate",
              position: "relative",
              maxWidth: 1328,
              display: "grid",
              gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
              columnGap: 2,
              height: 450,
              [theme.breakpoints.down("md")]: {
                gridTemplateColumns: "1fr",
                gridTemplateRows: "repeat(3, 1fr auto)",
                height: "auto",
              },
            }}
            disableGutters
          >
            <Box
              component="img"
              src="assets/imgs/webp/why-dna/why1.webp"
              alt="Why DNAcanvas image 1"
              sx={{
                position: "absolute",
                gridColumn: "1 / span 4",
                width: "calc(100% - 24px)",
                zIndex: 1,
                [theme.breakpoints.down("md")]: {
                  transform: "unset",
                  width: "100%",
                  position: "relative",
                  gridColumn: "1 / span 1",
                  placeSelf: "start start",
                },
              }}
            />
            <Box
              sx={{
                position: "absolute",
                gridColumn: "1 / span 3",
                placeSelf: "end",
                transform: "translateX(24px)",
                p: 4,
                pb: 5,
                backgroundColor: "info.200",
                borderRadius: "8px 56px",
                zIndex: 2,
                [theme.breakpoints.down("md")]: {
                  transform: "unset",
                  width: "100%",
                  position: "relative",
                  gridColumn: "1 / span 1",
                  placeSelf: "start center",
                  maxWidth: 312,
                  top: -24,
                },
              }}
            >
              <Typography variant="h3" color="text.darkblue">
                {t("why-heading.cards.0.title")}
              </Typography>
              <Typography variant="body2" color="text.darkblue">
                {t("why-heading.cards.0.description")}
              </Typography>
            </Box>
            <Box
              component="img"
              src="assets/imgs/webp/why-dna/why2.webp"
              alt="Why DNAcanvas image 2"
              sx={{
                position: "absolute",
                gridColumn: "4 / span 4",
                alignSelf: "end",
                placeSelf: "end",
                width: "calc(100% - 24px)",
                zIndex: 0,
                [theme.breakpoints.down("md")]: {
                  transform: "unset",
                  width: "100%",
                  position: "relative",
                  gridColumn: "1 / span 1",
                  placeSelf: "start start",
                },
              }}
            />
            <Box
              sx={{
                position: "absolute",
                gridColumn: "6 / span 3",
                transform: "translate(-32px, 48px)",
                p: 4,
                pb: 5,
                backgroundColor: "warning.200",
                borderRadius: "8px 56px",
                zIndex: 1,
                [theme.breakpoints.down("md")]: {
                  transform: "unset",
                  width: "100%",
                  position: "relative",
                  gridColumn: "1 / span 1",
                  placeSelf: "start center",
                  maxWidth: 312,
                  top: -24,
                },
              }}
            >
              <Typography variant="h3" color="text.darkblue">
                {t("why-heading.cards.1.title")}
              </Typography>
              <Typography variant="body2" color="text.darkblue">
                {t("why-heading.cards.1.description")}
              </Typography>
            </Box>
            <Box
              component="img"
              src="assets/imgs/webp/why-dna/why3.webp"
              alt="Why DNAcanvas image 3"
              sx={{
                position: "absolute",
                gridColumn: "8 / span 4",
                placeSelf: "end",
                width: "calc(100% - 24px)",
                height: "auto",
                transform: "translate(0px, -30px)",
                zIndex: 2,
                [theme.breakpoints.down("md")]: {
                  transform: "unset",
                  width: "100%",
                  position: "relative",
                  gridColumn: "1 / span 1",
                  placeSelf: "start start",
                },
              }}
            />
            <Box
              sx={{
                position: "absolute",
                gridColumn: "10 / span 3",
                placeSelf: "end",
                alignSelf: "end",
                width: 312,
                p: 4,
                pb: 5,
                backgroundColor: "success.100",
                borderRadius: "8px 56px",
                zIndex: 3,
                [theme.breakpoints.down("md")]: {
                  transform: "unset",
                  width: "100%",
                  position: "relative",
                  gridColumn: "1 / span 1",
                  placeSelf: "start center",
                  maxWidth: 312,
                  top: -24,
                },
              }}
            >
              <Typography variant="h3" color="text.darkblue">
                {t("why-heading.cards.2.title")}
              </Typography>
              <Typography variant="body2" color="text.darkblue">
                {t("why-heading.cards.2.description")}
              </Typography>
            </Box>
            <Box
              sx={{
                position: "absolute",
                gridColumn: "8 / span 4",
                width: "100%",
                transform: "translate(3.33vw, -10px)",
                zIndex: 0,
                [theme.breakpoints.down("md")]: {
                  width: "100%",
                  transform: "translate(-25%, 70px)",
                  gridColumn: "1 / span 1",
                  gridRow: "2 / span 1",
                  zIndex: -1,
                },
              }}
            >
              <DashedCurveDecoration />
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};
